import { Link } from "@inertiajs/react"

import { RowList } from "@/components/steel/table"
import { Section, StatusBadge, Tile, TileGrid } from "@/components/steel"
import { formatCurrency, shortMoney } from "@/lib/utils"
import type { Billing, InvoiceRow } from "@/pages/Projects/tabs/types"

function formatDate(value: string | null) {
  if (!value) return null
  return new Date(`${value}T00:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

export function BillingTab({
  billing,
  invoices,
  projectId,
}: {
  billing: Billing
  invoices: InvoiceRow[]
  projectId: string
}) {
  const unbilled = billing.unbilledLabor + billing.unbilledExpenses
  const payrollBasis =
    billing.payrollCostActual === 0
      ? "estimated"
      : billing.payrollCostEstimated === 0
        ? "actual"
        : "part actual"

  return (
    <Section className="pb-7">
      <div className="mb-5">
        <TileGrid>
          <Tile
            accent
            label="Contract value"
            value={formatCurrency(billing.contractValue)}
            sub="Total project revenue"
          />
          <Tile
            label="Billed to date"
            value={formatCurrency(billing.billedToDate)}
            sub={`${shortMoney(billing.outstandingAr)} outstanding`}
          />
          <Tile
            label="Payments received"
            value={formatCurrency(billing.paymentsReceived)}
            sub={`${shortMoney(unbilled)} accrued, unbilled`}
          />
          <Tile
            label="Margin"
            value={formatCurrency(billing.margin)}
            sub={`Billed less ${formatCurrency(billing.payrollCost)} payroll (${payrollBasis})`}
          />
        </TileGrid>
      </div>

      <div className="mb-3 flex items-baseline justify-between">
        <h2 className="text-title font-semibold">Invoices</h2>
        <Link
          href={`/projects/${projectId}/invoices/new`}
          className="text-label text-muted-foreground hover:text-foreground"
        >
          New invoice
        </Link>
      </div>

      {invoices.length === 0 ? (
        <p className="border-t border-border py-8 text-center text-label text-muted-foreground">
          No invoices on this project yet.
        </p>
      ) : (
        <RowList>
          {invoices.map((invoice) => (
            <Link
              key={invoice.id}
              href={`/invoices/${invoice.id}`}
              className="flex w-full items-center gap-3 border-t border-border px-0.5 py-2.5 text-left hover:bg-secondary"
            >
              <span className="min-w-0 flex-1">
                <span className="num block text-body font-medium">{invoice.number}</span>
                <span className="mt-0.5 block text-meta text-muted-foreground">
                  {formatDate(invoice.issueDate) ? `Issued ${formatDate(invoice.issueDate)}` : "Not issued"}
                  {formatDate(invoice.dueDate) ? ` · due ${formatDate(invoice.dueDate)}` : ""}
                </span>
              </span>
              <span className="num text-body">{formatCurrency(invoice.total)}</span>
              <StatusBadge status={invoice.status} />
            </Link>
          ))}
        </RowList>
      )}
    </Section>
  )
}
